import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { School, GraduationCap, ArrowLeft } from "lucide-react";
import PostCard from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";

function initials(name = "?") { return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }

export default function GroupDetail({ kind }) {
  const params = useParams();
  const [group, setGroup] = useState(null);

  useEffect(() => {
    const url = kind === "college"
      ? `/groups/college/${encodeURIComponent(params.college)}`
      : `/groups/batch/${params.year}`;
    api.get(url).then(({ data }) => setGroup(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [kind, params.college, params.year]);

  if (!group) return <div className="text-center py-16 font-serif-display text-2xl text-[color:var(--ayur-emerald)]">Loading group…</div>;

  const title = kind === "college" ? group.name : `Batch of ${group.year}`;
  const Icon = kind === "college" ? School : GraduationCap;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Link to="/app/groups" className="inline-flex items-center gap-1 text-sm text-[color:var(--ayur-emerald)] link-underline">
        <ArrowLeft className="w-4 h-4" /> All groups
      </Link>

      <div className="paper-card p-6 flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full ${kind === "college" ? "bg-[color:var(--ayur-emerald)]/10" : "bg-[color:var(--ayur-terracotta)]/10"} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${kind === "college" ? "text-[color:var(--ayur-emerald)]" : "text-[color:var(--ayur-terracotta)]"}`} />
        </div>
        <div>
          <div className="label-eyebrow">{kind === "college" ? "College group" : "Batch group"}</div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-[color:var(--ayur-emerald)]">{title}</h1>
          <div className="text-sm text-[color:var(--ayur-mute)]">{group.members.length} scholars · {group.posts.length} posts</div>
        </div>
      </div>

      <Tabs defaultValue="posts">
        <TabsList className="bg-[color:var(--ayur-cream-2)]">
          <TabsTrigger value="posts" data-testid="group-tab-posts">Posts</TabsTrigger>
          <TabsTrigger value="members" data-testid="group-tab-members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="space-y-4 mt-4">
          {group.posts.length === 0 && (
            <div className="paper-card p-12 text-center">
              <div className="label-eyebrow">Quiet here</div>
              <h3 className="font-serif-display text-2xl mt-2 text-[color:var(--ayur-emerald)]">No posts yet from this group</h3>
              <p className="text-[color:var(--ayur-ink-2)] mt-2">Follow classmates and start a conversation.</p>
            </div>
          )}
          {group.posts.map((p) => <PostCard key={p.id} post={p} />)}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {group.members.map((m) => (
              <Link key={m.id} to={`/app/scholars/${m.id}`} data-testid="group-member-card"
                className="paper-card p-4 flex items-center gap-3 hover:bg-white transition">
                <Avatar className="w-11 h-11">
                  <AvatarImage src={resolveImageUrl(m.avatar_url)} />
                  <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] font-semibold">
                    {initials(m.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate inline-flex items-center gap-1">
                    {m.full_name}
                    {m.verified && <VerifiedBadge size={14} />}
                  </div>
                  <div className="text-xs text-[color:var(--ayur-mute)] truncate">
                    {m.degree}{m.specialty ? ` · ${m.specialty}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
